import {
  Alert,
  AlertTitle,
  Box,
  Stack,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type { CSSObject } from 'tss-react';
import { useTranslation } from 'react-i18next';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { Flooding } from 'features/flooding/flooding.types';
import { RichTextContent } from 'components/RichTextContent';
import { getEvacZoneStyleFromGeoEvent } from 'shared/utils';
import { EvacZoneStatus, RegionEvacZoneStyle } from 'shared/types';
import {
  EVAC_ZONE_LABEL_TRANS_KEYS,
  EVAC_ZONE_LAYER_COLORS,
  EvacZoneStyleStatusMapping,
} from '../../../../constants';

type GeoEventEvacuationInfoProps = {
  geoEvent: Wildfire | Flooding;
};

export const hasGeoEventEvacuationInfo = (
  geoEvent: Wildfire | Flooding,
): boolean => {
  const {
    evacuationOrders,
    evacuationWarnings,
    evacuationAdvisories,
    evacuationShelterInPlace,
    evacuationNotes,
  } = geoEvent.data;

  return Boolean(
    evacuationOrders ||
      evacuationWarnings ||
      evacuationNotes ||
      evacuationAdvisories ||
      evacuationShelterInPlace,
  );
};

export const EVAC_ZONE_BACKGROUND_COLORS: EvacZoneStyleStatusMapping = {
  order_warning_advisory: {
    orders: '#FDECEA',
    warnings: '#FFF2BB',
    advisories: '#E8F2FD',
    shelter_in_place: '#E9D3FF',
  },
  ready_set_go: {
    orders: '#FDECEA',
    warnings: '#FFF2BB',
    advisories: '#D2F4CD',
    shelter_in_place: '#E9D3FF',
  },
  ready_set_go_with_levels: {
    orders: '#FDECEA',
    warnings: '#FFF2BB',
    advisories: '#D2F4CD',
    shelter_in_place: '#E9D3FF',
  },
  ready_set_go_reverse_colors: {
    orders: '#D2F4CD',
    warnings: '#FFF2BB',
    advisories: '#FDECEA',
    shelter_in_place: '#E9D3FF',
  },
  ready_set_shelter: {
    orders: '#FDECEA',
    warnings: '#FFF2BB',
    advisories: '#D2F4CD',
    shelter_in_place: '#E9D3FF',
  },
} as const;

export const EVAC_ZONE_BACKGROUND_COLORS_DARK: EvacZoneStyleStatusMapping = {
  order_warning_advisory: {
    orders: '#4A2422',
    warnings: '#4A4020',
    advisories: '#1E3442',
    shelter_in_place: '#3B2B4A',
  },
  ready_set_go: {
    orders: '#4A2422',
    warnings: '#4A4020',
    advisories: '#2B3F28',
    shelter_in_place: '#3B2B4A',
  },
  ready_set_go_with_levels: {
    orders: '#4A2422',
    warnings: '#4A4020',
    advisories: '#2B3F28',
    shelter_in_place: '#3B2B4A',
  },
  ready_set_go_reverse_colors: {
    orders: '#2B3F28',
    warnings: '#4A4020',
    advisories: '#4A2422',
    shelter_in_place: '#3B2B4A',
  },
  ready_set_shelter: {
    orders: '#4A2422',
    warnings: '#4A4020',
    advisories: '#2B3F28',
    shelter_in_place: '#3B2B4A',
  },
} as const;

const useStyles = makeStyles()((theme: Theme) => ({
  alert: {
    paddingBottom: 0,
    color: theme.palette.common.black,
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[100],
    }),
    '& .MuiAlert-message': {
      paddingBottom: 0,
    },
    '& .MuiAlert-icon': {
      paddingTop: 10,
    },
  } as CSSObject,
  icon: {
    width: 16,
    height: 16,
    borderRadius: 8,
    border: '1px solid #ffffff',
  },
}));

const AlertIcon = (props: { color: string }): JSX.Element => {
  const { color } = props;
  const { classes } = useStyles();
  return <div className={classes.icon} style={{ backgroundColor: color }} />;
};

const EvacuationLevelInfo = (props: {
  style: RegionEvacZoneStyle;
  level: EvacZoneStatus;
  text: string | null;
}): JSX.Element | null => {
  const { style: evacZoneStyle, level, text: evacuationText } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  if (!evacuationText) {
    return null;
  }

  return (
    <Alert
      severity="info"
      className={classes.alert}
      icon={false}
      sx={[
        {
          backgroundColor: EVAC_ZONE_BACKGROUND_COLORS[evacZoneStyle][level],
          // Same as WildfireAutomatedDisclaimer: severity tints (e.g. error/success) must not override body copy on custom fills.
          color: 'text.primary',
          '& .MuiAlert-message': { color: 'text.primary', width: '100%' },
        },
        (theme) =>
          theme.applyStyles('dark', {
            backgroundColor:
              EVAC_ZONE_BACKGROUND_COLORS_DARK[evacZoneStyle][level],
          }),
      ]}
    >
      <AlertTitle>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <AlertIcon color={EVAC_ZONE_LAYER_COLORS[evacZoneStyle][level]} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {t(EVAC_ZONE_LABEL_TRANS_KEYS[evacZoneStyle][level])}
          </Typography>
        </Stack>
      </AlertTitle>
      <Box sx={{ marginTop: -1 }}>
        <RichTextContent content={evacuationText} />
      </Box>
    </Alert>
  );
};

export const GeoEventEvacuationInfo = (
  props: GeoEventEvacuationInfoProps,
): JSX.Element | null => {
  const { geoEvent } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const {
    data: {
      evacuationOrders,
      evacuationWarnings,
      evacuationAdvisories,
      evacuationShelterInPlace,
      evacuationNotes,
    },
  } = geoEvent;

  const evacZoneStyle = getEvacZoneStyleFromGeoEvent(geoEvent);

  if (!hasGeoEventEvacuationInfo(geoEvent)) {
    return null;
  }

  return (
    <Stack spacing={1} data-testid="evacInfo">
      <EvacuationLevelInfo
        style={evacZoneStyle}
        level="orders"
        text={evacuationOrders}
      />
      <EvacuationLevelInfo
        style={evacZoneStyle}
        level="warnings"
        text={evacuationWarnings}
      />
      <EvacuationLevelInfo
        style={evacZoneStyle}
        level="advisories"
        text={evacuationAdvisories}
      />
      <EvacuationLevelInfo
        style={evacZoneStyle}
        level="shelter_in_place"
        text={evacuationShelterInPlace}
      />

      {evacuationNotes && (
        <Alert
          severity="info"
          className={classes.alert}
          icon={false}
          sx={{
            backgroundColor: 'background.inlineInfoCard',
            color: 'text.primary',
            '& .MuiAlert-message': { color: 'text.primary', width: '100%' },
          }}
        >
          <AlertTitle>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('evacZones.notes')}
            </Typography>
          </AlertTitle>
          <Box sx={{ marginTop: -1 }}>
            <RichTextContent content={evacuationNotes} />
          </Box>
        </Alert>
      )}
    </Stack>
  );
};
